import React, { useState, useMemo } from 'react';
import { FaRing, FaRupeeSign } from 'react-icons/fa';

// Helper to format numbers as Indian Rupee
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
};

function ChildMarriageCalculator() {
  // --- Input States ---
  const [childAge, setChildAge] = useState(5);
  const [marriageAge, setMarriageAge] = useState(25);
  const [currentCost, setCurrentCost] = useState(1500000); // 15 Lakhs
  const [inflationRate, setInflationRate] = useState(7); // Marriage inflation
  const [expectedReturn, setExpectedReturn] = useState(12); // Investment return
  
  // --- Calculation Logic ---
  const results = useMemo(() => {
    // 1. Time calculations
    const yearsToMarriage = marriageAge - childAge;
    
    // 2. Future Cost of Marriage
    const futureCost = currentCost * (1 + inflationRate / 100) ** yearsToMarriage;
    
    // 3. Monthly SIP Required to reach the future cost
    const i = expectedReturn / 100 / 12; // Monthly return rate
    const N = yearsToMarriage * 12; // Number of months to invest
    
    // If years are 0 or less, SIP is not applicable
    if (N <= 0) {
      return {
        futureCost: Math.round(currentCost),
        monthlySip: 0,
        totalInvested: 0,
        totalGains: 0,
        yearsToMarriage: 0,
      };
    }

    // Future Value of SIP formula, solved for Monthly Payment (SIP)
    const monthlySip = (futureCost * i) / (((1 + i) ** N - 1) * (1 + i));
    
    const totalInvested = monthlySip * N;
    const totalGains = futureCost - totalInvested;

    return {
      futureCost: Math.round(futureCost),
      monthlySip: Math.round(monthlySip),
      totalInvested: Math.round(totalInvested),
      totalGains: Math.round(totalGains),
      yearsToMarriage,
    };
  }, [childAge, marriageAge, currentCost, inflationRate, expectedReturn]);

  return (
    <div className="bg-gray-50 p-6 rounded-lg border">
      <div className="flex items-center mb-6">
        <FaRing className="h-8 w-8 text-blue-600 mr-3" />
        <h3 className="text-2xl font-bold text-gray-900">Child Marriage Calculator</h3>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* --- Column 1: Inputs --- */}
        <div className="space-y-6">
          {/* Child's Present Age */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Child's Present Age</label>
              <span className="text-xl font-bold text-blue-600">{childAge} Years</span>
            </div>
            <input
              type="range" min="0" max="25" step="1"
              value={childAge}
              onChange={(e) => setChildAge(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Age for Marriage */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Age for Marriage</label>
              <span className="text-xl font-bold text-blue-600">{marriageAge} Years</span>
            </div>
            <input
              type="range" min="18" max="40" step="1"
              value={marriageAge}
              onChange={(e) => setMarriageAge(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Current Cost of Marriage */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Current Cost of Marriage</label>
              <span className="text-xl font-bold text-blue-600">{formatCurrency(currentCost)}</span>
            </div>
            <input
              type="range" min="100000" max="10000000" step="100000"
              value={currentCost}
              onChange={(e) => setCurrentCost(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Marriage Inflation Rate */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Marriage Inflation Rate</label>
              <span className="text-xl font-bold text-blue-600">{inflationRate}%</span>
            </div>
            <input
              type="range" min="4" max="15" step="0.5"
              value={inflationRate}
              onChange={(e) => setInflationRate(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Expected Returns on Investment */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Expected Returns on Investment</label>
              <span className="text-xl font-bold text-blue-600">{expectedReturn}%</span>
            </div>
            <input
              type="range" min="5" max="20" step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* --- Column 2: Results --- */}
        <div className="bg-white rounded-xl p-6 border-2 border-blue-600 shadow-xl">
          <h3 className="text-xl font-bold text-blue-700 text-center">Your Child's Marriage Goal</h3>
          
          <div className="text-center my-6">
            <p className="text-lg text-gray-600">You will need a corpus of</p>
            <p className="text-5xl font-extrabold text-gray-900 my-2">
              {formatCurrency(results.futureCost)}
            </p>
            <p className="text-base text-gray-500">
              after {results.yearsToMarriage} years.
            </p>
          </div>
          
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-lg font-semibold text-gray-800 text-center">Required Investment</h4>
            
            <p className="text-3xl font-bold text-blue-600 text-center my-3">
              {formatCurrency(results.monthlySip)}
            </p>
            <p className="text-base text-gray-600 text-center">
              per month for the next {results.yearsToMarriage} years.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mt-6 space-y-3">
            <div className="flex justify-between text-base">
              <span className="text-gray-600">Total Amount Invested</span>
              <span className="font-bold text-gray-800">{formatCurrency(results.totalInvested)}</span>
            </div>
            <div className="flex justify-between text-base">
              <span className="text-gray-600">Estimated Gains</span>
              <span className="font-bold text-green-600">{formatCurrency(results.totalGains)}</span>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}

export default ChildMarriageCalculator;